import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle, Download, Gift, ArrowRight } from 'lucide-react';
import axios from 'axios';
import PromptVault from './PromptVault';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/payments/checkout/status/${sessionId}`
      );

      if (data.payment_status === 'paid') {
        setPaymentDetails(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status check error:', error);
      // Error checking status - show error UI
      setStatus('error');
    }
  }, [sessionId, BACKEND_URL]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId, pollPaymentStatus]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 pb-20 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your purchase</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 pb-20 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-2 border-red-200">
          <CardHeader>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <CardTitle className="text-center text-2xl">Payment Issue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-600">
              There was an issue processing your payment. Please try again or contact support.
            </p>
            <Button
              onClick={() => navigate('/marketplace')}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
            >
              Return to Marketplace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDownload = () => {
    if (paymentDetails?.download_token) {
      window.location.href = `${BACKEND_URL}/api/download/${paymentDetails.download_token}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 pb-20 px-4">
      <Card className="max-w-md w-full mx-auto border-2 border-green-200">
        <CardHeader>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <CardTitle className="text-center text-2xl">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-semibold">
                ${(paymentDetails?.amount_total / 100).toFixed(2)} {paymentDetails?.currency?.toUpperCase()}
              </span>
            </div>
            {paymentDetails?.product_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Product:</span>
                <span className="font-semibold text-sm">{paymentDetails.product_name}</span>
              </div>
            )}
            {paymentDetails?.metadata?.quantity && (
              <div className="flex justify-between">
                <span className="text-gray-600">Quantity:</span>
                <span className="font-semibold">{paymentDetails.metadata.quantity}</span>
              </div>
            )}
          </div>

          {paymentDetails?.download_token && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-2 border-green-200">
              <p className="text-center text-green-800 font-semibold mb-3 text-sm">
                🎉 Your product is ready for download!
              </p>
              <Button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-3"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Your Product Now
              </Button>
            </div>
          )}

          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Thank you for your purchase! You can download your product immediately using the button above.
            </p>
          </div>

          <div className="space-y-2">
            <Button
              onClick={() => navigate('/marketplace')}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
            >
              Continue Shopping
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              Return to Home
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 🎁 Prompt Vault unlocked for this product */}
      {paymentDetails?.metadata?.product_id && (
        <div className="max-w-5xl mx-auto mt-10">
          <Card className="bg-gradient-to-br from-orange-500/15 to-amber-500/10 border-2 border-orange-500/40 shadow-2xl mb-6">
            <CardContent className="p-6 text-center">
              <Gift className="w-10 h-10 text-orange-500 mx-auto mb-2" />
              <h2 className="text-2xl font-black text-gray-900 mb-1">🎁 BONUS UNLOCKED</h2>
              <p className="text-gray-700 text-sm mb-3">
                Your purchase includes <strong>15 ready-to-paste AI prompts</strong> — scroll down to use them.
              </p>
              <Link to={`/prompt-vault/${paymentDetails.metadata.product_id}`}>
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold">
                  Open Full Vault Page <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
          <PromptVault productIdOverride={paymentDetails.metadata.product_id} embedded={true} />
        </div>
      )}
    </div>
  );
};

export default CheckoutSuccess;
